import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTokens } from 'theme';
import Panel from './Panel';
import { fmtRelative, fmtDate } from './format';
import { ChannelBadge, ChannelSelector } from './channels';
import {
  useApiKeys,
  useCreateApiKey,
  useUpdateApiKey,
  useDeleteApiKey,
} from 'api/bi';
import { extractApiError } from 'api/errors';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import useRole from 'hooks/useRole';

import { useTranslation } from 'react-i18next';

function parseScopes(str) {
  const parts = (str || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length || parts.includes('*')) return ['*'];
  return parts;
}
function scopesToStr(scopes) {
  if (!scopes || scopes.includes('*')) return '*';
  return scopes.join(', ');
}
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}
function localInputToIso(val) {
  return val ? new Date(val).toISOString() : null;
}

function ScopeChips({ scopes }) {
  const { t } = useTranslation();
  if (!scopes || scopes.includes('*')) {
    return <Chip label={t('data.allAccess')} size="small" color="primary" />;
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {scopes.map((s) => (
        <Chip key={s} label={s} size="small" sx={{ fontFamily: 'var(--nb-mono)' }} />
      ))}
    </Stack>
  );
}

function TokensPanelContent() {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const { data: keys, isLoading } = useApiKeys();
  const createKey = useCreateApiKey();
  const updateKey = useUpdateApiKey();
  const deleteKey = useDeleteApiKey();

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: '*', expires: '', channels: ['cube', 'mcp'] });
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', scopes: '', expires: '' });
  const [editError, setEditError] = useState(null);

  const [busyId, setBusyId] = useState(null);
  const [toDelete, setToDelete] = useState(null); // key pending revoke / permanent delete
  const [deleteError, setDeleteError] = useState(null);

  const openCreate = () => {
    setForm({ name: '', scopes: '*', expires: '', channels: ['cube', 'mcp'] });
    setCreatedKey(null);
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreate = () => setCreateOpen(false);

  // On exit, not on close: the dialog must not flip back to the form
  // mid-transition. The raw key leaves state and mutation cache here (show-once).
  const onCreateExited = () => {
    setCreatedKey(null);
    createKey.reset();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setCreateError(null);
    try {
      const result = await createKey.mutateAsync({
        name: form.name.trim(),
        scopes: parseScopes(form.scopes),
        channels: form.channels,
        expires_at: localInputToIso(form.expires),
      });
      setCreatedKey(result);
    } catch (err) {
      setCreateError(extractApiError(err, t('data.failedToCreateApiKey')));
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
      setCreateError(t('connectionGuide.copyFailed'));
    }
  };

  const openEdit = (key) => {
    setEditKey(key);
    setEditForm({
      name: key.name,
      scopes: scopesToStr(key.scopes),
      expires: isoToLocalInput(key.expires_at),
      channels: key.channels?.length ? key.channels : ['cube', 'mcp'],
    });
    setEditError(null);
  };

  const handleUpdate = async () => {
    setEditError(null);
    try {
      await updateKey.mutateAsync({
        id: editKey.id,
        name: editForm.name.trim(),
        scopes: parseScopes(editForm.scopes),
        channels: editForm.channels,
        expires_at: localInputToIso(editForm.expires),
      });
      setEditKey(null);
    } catch (err) {
      setEditError(extractApiError(err, t('data.failedToUpdateKey')));
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const revoking = toDelete.is_active;
    setBusyId(toDelete.id);
    setDeleteError(null);
    try {
      await deleteKey.mutateAsync({ id: toDelete.id, purge: !revoking });
      setToDelete(null);
    } catch (err) {
      setDeleteError(extractApiError(err, t('data.actionFailedPleaseTryAgain')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      icon={<KeyIcon />}
      title={t('data.apiTokens')}
      action={(
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >{t('data.newToken')}</Button>
      )}
      bodySx={{ p: 0 }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : !keys?.length ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <KeyIcon sx={{ fontSize: 40, color: themeTokens.inkMuted, mb: 1 }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: themeTokens.inkMuted, mb: 0.5 }}>{t('data.noApiTokensYet')}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: themeTokens.inkMuted, mb: 2 }}>{t('data.createATokenToLetExternalServicesQueryYourBiApi')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>{t('data.createYourFirstToken')}</Button>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('data.name')}</TableCell>
                <TableCell>{t('data.prefix')}</TableCell>
                <TableCell>{t('data.access')}</TableCell>
                <TableCell>{t('data.scopes')}</TableCell>
                <TableCell>{t('data.status')}</TableCell>
                <TableCell align="right">{t('data.queries')}</TableCell>
                <TableCell>{t('data.lastUsed')}</TableCell>
                <TableCell>{t('data.expires')}</TableCell>
                <TableCell align="right">{t('data.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{key.name}</Typography>
                    {key.created_by_email && (
                      <Typography sx={{ fontSize: '0.68rem', color: themeTokens.inkMuted }}>{key.created_by_email}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography className="mono" sx={{ fontSize: '0.78rem' }}>{key.key_prefix}…</Typography></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {(key.channels || []).map((c) => <ChannelBadge key={c} channel={c} />)}
                    </Stack>
                  </TableCell>
                  <TableCell><ScopeChips scopes={key.scopes} /></TableCell>
                  <TableCell>
                    <Chip label={!key.is_active ? t('data.revoked') : key.expires_at && Date.parse(key.expires_at) <= Date.now() ? t('connections.expired') : t('data.active')} size="small" color={!key.is_active ? 'default' : key.expires_at && Date.parse(key.expires_at) <= Date.now() ? 'warning' : 'success'} />
                  </TableCell>
                  <TableCell align="right"><Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{key.query_count ?? 0}</Typography></TableCell>
                  <TableCell><Typography sx={{ fontSize: '0.76rem', color: themeTokens.inkMuted }}>{fmtRelative(key.last_used_at)}</Typography></TableCell>
                  <TableCell><Typography sx={{ fontSize: '0.76rem', color: themeTokens.inkMuted }}>{key.expires_at ? fmtDate(key.expires_at) : t('data.never2')}</Typography></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                      {key.is_active && (
                        <Tooltip title={t('data.edit')}>
                          <IconButton size="small" onClick={() => openEdit(key)}><EditOutlinedIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {key.is_active ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={busyId === key.id}
                          startIcon={busyId === key.id ? <CircularProgress size={16} /> : <BlockIcon fontSize="small" />}
                          onClick={() => { setDeleteError(null); setToDelete(key); }}
                        >{t('data.revoke')}</Button>
                      ) : (
                        <Tooltip title={t('data.deletePermanently')}>
                          <span>
                            <IconButton size="small" color="error" aria-label={t('data.deletePermanently')} disabled={busyId === key.id} onClick={() => { setDeleteError(null); setToDelete(key); }}>
                              {busyId === key.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={createKey.isPending ? undefined : closeCreate}
        TransitionProps={{ onExited: onCreateExited }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{createdKey ? t('data.tokenCreated') : t('data.createApiToken')}</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          {createdKey ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>{t('data.copyThisTokenNowItWillNotBeShownAgain')}</Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: themeTokens.raised, borderRadius: 1, fontFamily: 'var(--nb-mono)', wordBreak: 'break-all', fontSize: 13 }}>
                {createdKey.key}
                <IconButton size="small" onClick={handleCopy}>
                  {copied ? <CheckIcon fontSize="small" sx={{ color: themeTokens.positive }} /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Box>
              {copied && <Typography variant="caption" color="success.main">{t('data.copied')}</Typography>}
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label={t('data.tokenName')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth placeholder={t('data.eGProductionDashboard')} autoFocus />
              <TextField
                label={t('data.scopes')}
                value={form.scopes}
                onChange={(e) => setForm({ ...form, scopes: e.target.value })}
                fullWidth
                helperText={t('data.forFullAccessOrCommaSeparatedCubeNamesEGSales')}
              />
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: themeTokens.inkMuted, mb: 0.75 }}>{t('data.access')}</Typography>
                <ChannelSelector value={form.channels} onChange={(channels) => setForm({ ...form, channels })} />
                <Typography sx={{ fontSize: '0.72rem', color: themeTokens.inkMuted, mt: 0.5 }}>{t('data.whichSurfacesThisTokenMayUseTrinoIsRawDwhSql')}</Typography>
              </Box>
              <TextField
                label={t('data.expiresOptional')}
                type="datetime-local"
                value={form.expires}
                onChange={(e) => setForm({ ...form, expires: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {createdKey ? (
            <Button onClick={closeCreate}>{t('data.done')}</Button>
          ) : (
            <>
              <Button onClick={closeCreate} disabled={createKey.isPending}>{t('data.cancel')}</Button>
              <Button variant="contained" onClick={handleCreate} disabled={createKey.isPending || !form.name.trim()}>
                {createKey.isPending ? t('data.creating') : t('data.createToken')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editKey} onClose={() => setEditKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('data.editToken')}</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('data.tokenName')} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth />
            <TextField label={t('data.scopes')} value={editForm.scopes} onChange={(e) => setEditForm({ ...editForm, scopes: e.target.value })} fullWidth helperText={t('data.forFullAccessOrCommaSeparatedCubeNames')} />
            <Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: themeTokens.inkMuted, mb: 0.75 }}>{t('data.access')}</Typography>
              <ChannelSelector value={editForm.channels} onChange={(channels) => setEditForm({ ...editForm, channels })} />
            </Box>
            <TextField label={t('data.expiresOptional')} type="datetime-local" value={editForm.expires} onChange={(e) => setEditForm({ ...editForm, expires: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditKey(null)}>{t('data.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={updateKey.isPending || !editForm.name.trim()}>
            {updateKey.isPending ? t('data.saving') : t('data.saveChanges2')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmActionDialog
        open={!!toDelete}
        title={toDelete?.is_active ? t('data.revokeToken') : t('data.deleteToken')}
        confirmWord={toDelete?.is_active ? t('data.revoke2') : t('data.delete')}
        confirmLabel={toDelete?.is_active ? t('data.revokeToken') : t('data.deleteToken')}
        confirmColor="error"
        loading={!!toDelete && busyId === toDelete.id}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      >
        <Typography variant="body2">
          {toDelete?.is_active ? (
            <>{t('data.revoke')}{' '}<strong>{toDelete?.name}</strong>{t('data.anyServiceUsingThisKeyStopsWorkingImmediatelyTheTokenStays')}</>
          ) : (
            <>{t('data.permanentlyDelete')}{' '}<strong>{toDelete?.name}</strong>{t('data.thisRemovesTheTokenRecordForGoodAndCannotBeUndone')}</>
          )}
        </Typography>
      </ConfirmActionDialog>
    </Panel>
  );
}

export default function TokensPanel() {
  const { isAdmin } = useRole();
  return isAdmin ? <TokensPanelContent /> : null;
}
